import { store } from '@/app-storage/store.ts'
import { AuthService } from '@/app/pages/authentication/AuthService'
import { AUTH_MODE, keycloakAuthService } from '@/auth/keycloak'
import { ENV, VALID_VENDORS_SUBDOMAINS } from '@/common/constants.ts'
import { getSubdomain } from '@/common/utilities.ts'
import * as Sentry from '@sentry/react'
import axios from 'axios'
import i18n from 'i18next'

const axiosInstance = axios.create({
  baseURL: ENV.VITE_API_URL,
  timeout: 30000, // Timeout in milliseconds
  headers: {
    'Content-Type': 'application/json',
  },
})

axiosInstance.interceptors.request.use(
  async (config) => {
    const subDomain = getSubdomain()
    let globalOnId = ENV.VITE_WALLET_GLOBAL_ON_ID
    if (subDomain && VALID_VENDORS_SUBDOMAINS.includes(subDomain)) {
      globalOnId = ENV.VITE_VENDORS_GLOBAL_ON_ID
    }

    const publicRoutes = [
      '/complete_registration',
      '/email_verification',
      '/maintenance',
    ]
    const publicRoute = publicRoutes.some((route) =>
      config.url?.includes(route)
    )

    //Check if the prefix ‘scholar’ is not at the start of the route.
    if (
      !config?.url?.startsWith('/admin') &&
      !config?.url?.startsWith('access') &&
      !config?.url?.startsWith('/public') &&
      !config?.url?.startsWith('/applicants') &&
      !config?.url?.startsWith('/scholar') &&
      !config?.url?.startsWith('/temp-public-download-file') &&
      !config?.url?.startsWith('/transfer_scholars') &&
      !config?.url?.startsWith('/maintenance')
    ) {
      config.url = `/scholar${config.url}`
    }

    // Check if config.params already exists
    if (config.params) {
      // Append the global_on_id parameter to existing params
      config.params.global_on_id = globalOnId
    } else {
      // Create a new params object with the global_on_id parameter
      config.params = { global_on_id: globalOnId }
    }

    if (i18n?.language != undefined) {
      config.params.i18n = i18n.language
    }

    const AuthSession = store.getState().AuthSession
    if (AuthSession.auth_badge.access_token != '' && !publicRoute) {
      config.headers.Authorization = `Bearer ${AuthSession.auth_badge.access_token}`
    }

    if(!publicRoute){
      if (AUTH_MODE === 'keycloak') {
        if (keycloakAuthService().isAuthenticated() && config?.headers) {
          await keycloakAuthService().ensureFreshToken(30)
          const token = keycloakAuthService().getAccessToken()  
          config.headers.Authorization = `Bearer ${token}`
        }
      } else {
        const AuthSession = store.getState().AuthSession
        if (AuthSession.auth_badge.access_token != '') {
          config.headers.Authorization = `Bearer ${AuthSession.auth_badge.access_token}`
        }
      }
    }

    return config
  },
  (error) => {
    // Do something with request error

    Sentry.captureException(error)
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.response.use(
  (response) => {
    // Do something with response data
    return response
  },
  async (error) => {
    try {
      return await AuthService.handleAuthError(error)
    } catch (err) {
      Sentry.captureException(error, {
        extra: {
          message: error.response?.data?.message ?? 'No error message provided',
        },
      })
      return Promise.reject(err)
    }
  }
)

export default axiosInstance
